import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowRightIcon, CheckIcon, CopyIcon } from 'lucide-react';
import { GapTrack } from './GapTrack';
import { PortraitPolaroid } from './PortraitPolaroid';
import { clients, profile } from '../../data/profile';
import { EASE_OUT } from '../../utils/motion';
const HEADLINE_WORDS = ['I', 'design', 'products', 'people', 'actually', 'finish.'];
export function Hero() {
  const [copied, setCopied] = useState(false);
  const copyEmail = async () => {
    try {
      await navigator.clipboard.writeText(profile.email);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      window.location.href = `mailto:${profile.email}`;
    }
  };
  const fadeUp = (delay: number) => ({
    initial: {
      opacity: 0,
      y: 10
    },
    animate: {
      opacity: 1,
      y: 0
    },
    transition: {
      duration: 0.3,
      ease: EASE_OUT,
      delay
    }
  });
  return <section id="top" className="scroll-mt-24" data-id="element-194" data-mp="1q63ish9lq77d">
      <div className="lg:grid lg:grid-cols-[minmax(0,1fr)_240px] lg:items-start lg:gap-12" data-id="element-195" data-mp="1mvt35l9go6yw">
      <div className="min-w-0" data-id="element-196" data-mp="1rv5sqdrpvs56">
      <motion.div {...fadeUp(0)} className="mb-7 flex items-center gap-4 lg:hidden" data-id="element-197" data-mp="1rwgwwep2r1wt">
        <img src={profile.portrait} alt={profile.portraitAlt} className="h-16 w-16 shrink-0 rounded-full object-cover object-[center_30%]" data-id="element-198" data-mp="1cyy8jdgb6qpw" />
        <p className="text-sm leading-snug text-fg-faint" data-id="element-199" data-mp="25n3u22f6sjqs">{profile.portraitCaption}</p>
      </motion.div>

      <motion.p {...fadeUp(0)} className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.12em] text-rust" data-id="element-200" data-mp="2amgfciqetir9">
        <span aria-hidden className="h-[1.5px] w-[22px] bg-coral" data-id="element-201" data-mp="1vw952qak361n" />
        SENIOR PRODUCT DESIGNER · BEHAVIORAL SCIENCE · & AI
      </motion.p>

      <h1 className="mt-5 max-w-2xl text-[40px] font-semibold leading-[1.08] tracking-tight text-ink sm:text-5xl lg:text-[56px] dark:text-fg" data-id="element-202" data-mp="20tr8601w6r1q">
        {HEADLINE_WORDS.map((word, i) => <span key={word} className="mr-[0.24em] inline-block overflow-hidden pb-1 align-bottom last:mr-0" data-id="element-203" data-mp="2tehihxa7vv6h" data-mpi={i}>
            <motion.span className="inline-block" initial={{
              y: '105%'
            }} animate={{
              y: 0
            }} transition={{
              duration: 0.3,
              ease: EASE_OUT,
              delay: 0.08 + i * 0.05
            }} data-id="element-204" data-mp="3360wh1ezavpb">
              {word}
            </motion.span>
          </span>)}
      </h1>

      <motion.div {...fadeUp(0.38)} className="mt-6" data-id="element-205" data-mp="2k99agk81y2hk">
        <GapTrack data-id="element-206" data-mp="2kau3u6imrfdu" />
      </motion.div>

      <motion.p {...fadeUp(0.42)} className="mt-7 max-w-xl text-base leading-relaxed text-fg-muted" data-id="element-207" data-mp="2fj2kafxw69x7">
        I'm Kat, a senior product designer and psychology student with 9 years in fintech, B2B SaaS and AI. I use
        behavioral science to close the gap between what people plan to do and what they actually do.
      </motion.p>

      <motion.div {...fadeUp(0.48)} className="mt-8 flex flex-wrap items-center gap-x-7 gap-y-4" data-id="element-208" data-mp="21hq5i7ugwecw">
        <a href="#work" className="group inline-flex items-center gap-2 rounded-full bg-ink px-7 py-3.5 text-[15px] font-semibold text-white shadow-[0_4px_14px_rgba(30,75,72,0.22)] transition-[background-color,transform] duration-150 ease-out hover:bg-ink-deep active:scale-[0.97]" data-id="element-209" data-mp="1wl9i5fv3e40v">
          See case studies
          <ArrowRightIcon className="h-4 w-4 transition-transform duration-200 ease-out group-hover:translate-x-0.5" data-id="element-210" data-mp="ly91sd7tkpe0" />
        </a>

        <button type="button" onClick={copyEmail} aria-label={`Copy email address ${profile.email}`} className="group inline-flex items-center gap-2 py-0.5 text-[13.5px] font-medium text-fg-muted underline decoration-fg-faint/50 decoration-dotted underline-offset-4 transition-colors duration-150 hover:text-ink hover:decoration-ink/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink/30" data-id="element-211" data-mp="3phc3s95zvdbr">
          <span className={copied ? 'font-semibold text-ink' : ''} data-id="element-212" data-mp="36o86mdtkiice">{copied ? 'Copied!' : profile.email}</span>
          <span className="relative grid h-3.5 w-3.5 place-items-center" data-id="element-213" data-mp="3apjp7ze0y3b3">
            <AnimatePresence mode="popLayout" initial={false} data-id="element-214" data-mp="2rnklltvintrp">
              <motion.span key={copied ? 'check' : 'copy'} initial={{
                  opacity: 0,
                  scale: 0.8
                }} animate={{
                  opacity: 1,
                  scale: 1
                }} exit={{
                  opacity: 0,
                  scale: 0.8
                }} transition={{
                  duration: 0.15,
                  ease: EASE_OUT
                }} className="absolute" data-id="element-215" data-mp="2mozm5wo39e4b">
                {copied ? <CheckIcon className="h-3.5 w-3.5 text-ink" strokeWidth={2.4} data-id="element-216" data-mp="35ey02teki1fv" /> : <CopyIcon className="h-3.5 w-3.5 text-fg-faint transition-colors duration-150 group-hover:text-ink" data-id="element-217" data-mp="3kci2bsk2xiic" />}
              </motion.span>
            </AnimatePresence>
          </span>
        </button>
        <span aria-live="polite" className="sr-only" data-id="element-218" data-mp="1pyywlvfwatly">
          {copied ? 'Email copied' : ''}
        </span>
      </motion.div>
      </div>

      <motion.figure initial={{
        opacity: 0,
        scale: 0.96
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 0.3,
        ease: EASE_OUT,
        delay: 0.45
      }} className="hidden lg:block lg:pt-2" data-id="element-219" data-mp="1l177182q3jkx">
        <PortraitPolaroid data-id="element-220" data-mp="3mwwirr0wmc9k" />
        <figcaption className="mt-3 text-[13px] leading-relaxed text-fg-faint" data-id="element-221" data-mp="3mvlfrtfa98ji">{profile.portraitCaption}</figcaption>
      </motion.figure>
      </div>

      <motion.div {...fadeUp(0.54)} className="mt-12 max-w-[620px] border-t border-line pt-6" data-id="element-222" data-mp="3dvq13emiqveq">
        <p className="text-[11.5px] font-semibold uppercase tracking-[0.1em] text-fg-faint" data-id="element-223" data-mp="3hxbjtglqi3wu">Selected clients</p>
        <ul className="mt-4 flex flex-wrap gap-x-8 gap-y-3" data-id="element-224" data-mp="33xse0ugeb4np">
          {clients.map(client => <li key={client} className="text-[14.5px] font-semibold tracking-wide text-fg-faint" data-id="element-225" data-mp="38wdbim49ja8g">
              {client}
            </li>)}
        </ul>
      </motion.div>
    </section>;
}